import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, ChevronRight, SkipForward, Sparkles, ChevronLeft, EyeOff } from 'lucide-react';
import { useOnboarding } from '../hooks/useOnboarding';
import { useSessionOnboarding } from '../hooks/useSessionOnboarding';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  useSessionHook?: boolean; // Flag to determine which hook to use
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onClose, useSessionHook = false }) => {
  // Use the appropriate hook based on the flag
  const dashboardOnboarding = useOnboarding();
  const sessionOnboarding = useSessionOnboarding();
  
  const onboardingData = useSessionHook ? sessionOnboarding : dashboardOnboarding;
  
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isLastStep,
    nextStep,
    previousStep,
    skipOnboarding,
    dontShowAgain
  } = onboardingData;


  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const [modalPosition, setModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });

  // Function to update modal position based on current scroll
  const updateModalPosition = useCallback(() => {
    if (!currentStep?.targetElement || !highlightedElement) return;

    const element = highlightedElement;
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    let top = rect.top + scrollTop;
    let left = rect.left + rect.width / 2;
    
    // Adjust position based on step position preference
    switch (currentStep.position) {
      case 'top':
        top = rect.top + scrollTop - 20;
        break;
      case 'bottom':
        top = rect.bottom + scrollTop + 20;
        break;
      case 'left':
        left = rect.left - 480; // Even more space to the left to avoid overlap
        top = rect.top + scrollTop + rect.height / 2;
        break;
      case 'right':
        left = rect.right + 20;
        top = rect.top + scrollTop + rect.height / 2;
        break;
      case 'above':
        top = rect.top + scrollTop - 220;
        left = 20;
        break;
    }

    let modalStyles: any = {};
    
    if (currentStep.position === 'above') {
      modalStyles = {
        top: `${Math.max(20, top)}px`,
        left: '20px',
        right: '20px',
        width: 'calc(100% - 40px)',
        maxWidth: 'none',
        transform: 'none'
      };
    } else {
      modalStyles = {
        top: `${Math.max(20, Math.min(top, window.innerHeight - 400))}px`,
        left: `${Math.max(20, Math.min(left, window.innerWidth - 320))}px`,
        transform: currentStep.position === 'left' || currentStep.position === 'right' 
          ? 'translateY(-50%)' 
          : 'translateX(-50%)'
      };
    }

    setModalPosition(modalStyles);
  }, [currentStep, highlightedElement]);

  // Handle element highlighting and modal positioning
  useEffect(() => {
    if (!isOpen || !currentStep) return;

    // Remove previous highlight
    if (highlightedElement) {
      highlightedElement.classList.remove('onboarding-highlight');
    }

    // Center position for final celebratory card
    if (currentStep.position === 'center') {
      setModalPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setHighlightedElement(null);
      return;
    }

    // Reset modal position for non-targeted steps
    if (!currentStep.targetElement) {
      setModalPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setHighlightedElement(null);
      return;
    }

    // Find and highlight target element
    const element = document.querySelector(currentStep.targetElement);
    if (element) {
      element.classList.add('onboarding-highlight');
      setHighlightedElement(element);

      // Position modal relative to element
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      let top = rect.top + scrollTop;
      let left = rect.left + rect.width / 2;
      
      // Adjust position based on step position preference
      switch (currentStep.position) {
        case 'top':
          top = rect.top + scrollTop - 20;
          break;
        case 'bottom':
          top = rect.bottom + scrollTop + 20;
          break;
        case 'left':
          left = rect.left - 480; // Even more space to the left to avoid overlap
          top = rect.top + scrollTop + rect.height / 2;
          break;
        case 'right':
          left = rect.right + 20;
          top = rect.top + scrollTop + rect.height / 2;
          break;
        case 'above':
          // Position as a banner above the element with full width
          top = rect.top + scrollTop - 220; // Higher position
          left = 20; // Start from left edge with padding
          break;
      }

      let modalStyles: any = {};
      
      if (currentStep.position === 'above') {
        // Rectangle banner style for session cards
        modalStyles = {
          top: `${Math.max(20, top)}px`,
          left: '20px',
          right: '20px',
          width: 'calc(100% - 40px)',
          maxWidth: 'none',
          transform: 'none'
        };
      } else {
        // Normal modal positioning
        modalStyles = {
          top: `${Math.max(20, Math.min(top, window.innerHeight - 400))}px`,
          left: `${Math.max(20, Math.min(left, window.innerWidth - 320))}px`,
          transform: currentStep.position === 'left' || currentStep.position === 'right' 
            ? 'translateY(-50%)' 
            : 'translateX(-50%)'
        };
      }

      setModalPosition(modalStyles);

      // Enhanced scrolling control for perfect positioning
      const scrollToElement = () => {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        
        // Calculate optimal scroll position based on modal position
        let targetScrollTop;
        
        if (currentStep.position === 'above') {
          // For banner modals, position element lower in viewport
          targetScrollTop = absoluteElementTop - (window.innerHeight * 0.4);
        } else if (currentStep.position === 'bottom') {
          // For bottom positioned modals, show element in upper portion
          targetScrollTop = absoluteElementTop - (window.innerHeight * 0.25);
        } else {
          // For side modals, center the element
          targetScrollTop = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        }
        
        // Smooth scroll with easing
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });
      };

      // Use setTimeout to ensure element is rendered and highlighted before scrolling
      setTimeout(scrollToElement, 300);

      // Add scroll listener to update modal position
      const handleScroll = () => {
        updateModalPosition();
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }

    return () => {
      if (highlightedElement) {
        highlightedElement.classList.remove('onboarding-highlight');
      }
    };
  }, [currentStep, isOpen, highlightedElement, updateModalPosition]);

  if (!isOpen || !currentStep) return null;

  const handleNext = () => {
    nextStep();
    if (isLastStep) {
      onClose();
    }
  };

  const handleSkip = () => {
    skipOnboarding();
    onClose();
  };

  const handleDontShowAgain = () => {
    dontShowAgain();
    onClose();
  };


  const isFirstStep = currentStepIndex === 0;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-50"></div>
      <Card 
        className={`fixed shadow-2xl border-0 z-50 transition-all duration-700 ease-in-out ${
          currentStep?.position === 'above' 
            ? 'w-auto' // Let width be controlled by style
            : 'max-w-md w-full'
        } ${
          isLastStep 
            ? 'bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 border-2 border-green-200' 
            : 'bg-white'
        }`}
        style={modalPosition}
      >
        <CardHeader className={`${currentStep?.position === 'above' ? 'pb-4' : 'text-center space-y-4 pb-6'}`}>
          {currentStep?.position === 'above' ? (
            // Banner layout for session cards
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-gray-900 mb-1">
                    {currentStep.title}
                  </CardTitle>
                  <span className="text-sm text-gray-500 font-medium">
                    Step {currentStepIndex + 1} of {totalSteps}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-gray-400 hover:text-gray-600 p-1 h-auto"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            // Normal centered layout
            <>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-500 font-medium">
                    Step {currentStepIndex + 1} of {totalSteps}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-gray-400 hover:text-gray-600 p-1 h-auto"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                <CardTitle className="text-xl font-bold text-gray-900 leading-tight">
                  {currentStep.title}
                </CardTitle>
                <div className="w-16 h-1 bg-blue-600 rounded-full mx-auto"></div>
              </div>
            </>
          )}
        </CardHeader>

        <CardContent className={`${currentStep?.position === 'above' ? 'space-y-4' : 'space-y-6'}`}>
          {currentStep?.position === 'above' ? (
            // Banner content layout
            <div className="flex items-center justify-between gap-6">
              <div className="flex-1">
                <p className="text-gray-600 leading-relaxed text-left">
                  {currentStep.description}
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Progress indicator */}
                <div className="text-right">
                  <div className="text-xs text-gray-500 mb-1">Progress</div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">
                      {Math.round(((currentStepIndex + 1) / totalSteps) * 100)}%
                    </span>
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-2">
                  {!isFirstStep && (
                    <Button
                      variant="outline"
                      onClick={previousStep}
                      size="sm"
                      className="group"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
                      Back
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    size="sm"
                    className="group"
                  >
                    <SkipForward className="w-4 h-4 mr-1 group-hover:translate-x-1 transition-transform" />
                    Skip
                  </Button>
                  {isFirstStep && (
                    <Button
                      variant="ghost"
                      onClick={handleDontShowAgain}
                      size="sm"
                      className="text-gray-400 hover:text-gray-600 group"
                    >
                      <EyeOff className="w-4 h-4 mr-1" />
                      Don't show again
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    size="sm"
                    className={isLastStep 
                      ? "bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl group transform hover:scale-105" 
                      : "bg-blue-600 hover:bg-blue-700 group"
                    }
                  >
                    {isLastStep ? '🚀 Start Learning!' : 'Next'}
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Normal vertical layout
            <>
              <p className="text-gray-600 leading-relaxed text-center">
                {currentStep.description}
              </p>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Progress</span>
                  <span>{Math.round(((currentStepIndex + 1) / totalSteps) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    onClick={previousStep}
                    className="group"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                    Back
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="group"
                >
                  <SkipForward className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                  Skip Tour
                </Button>
                <Button
                  onClick={handleNext}
                  className={isLastStep 
                    ? "bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl group transform hover:scale-105" 
                    : "bg-blue-600 hover:bg-blue-700 group"
                  }
                >
                  {isLastStep ? '🚀 Let\'s Start!' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </>
          )}

          {/* Additional help text and options for first step */}
          {currentStepIndex === 0 && (
            <div className="text-center space-y-3">
              <p className="text-xs text-gray-500">
                This quick tour will help you get familiar with the platform
              </p>
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDontShowAgain}
                  className="text-xs text-gray-400 hover:text-gray-600 h-auto px-2 py-1"
                >
                  <EyeOff className="w-3 h-3 mr-1" />
                  Don't show this again
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Global CSS for highlighting and animations */}
      <style>{`
        .onboarding-highlight {
          position: relative;
          z-index: 1000;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 0 8px rgba(59, 130, 246, 0.2);
          border-radius: 8px;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .onboarding-highlight::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 12px;
          z-index: -1;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.7; 
          }
          50% { 
            transform: scale(1.05); 
            opacity: 0.3; 
          }
        }

        /* Enhanced modal animations */
        .onboarding-modal-enter {
          animation: modalSlideAndScale 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes modalSlideAndScale {
          0% {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Content fade transitions */
        .content-transition {
          animation: contentFade 0.4s ease-in-out;
        }

        @keyframes contentFade {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default OnboardingModal;